import { Editor, mergeAttributes } from '@tiptap/core';
import StarterKit from '@tiptap/starter-kit';
// webpack 5 honours the package `exports` field, so the bare specifier resolves to
// the ESM build (the only exported subpath is "."). This avoids the ESM-interop issue
// that previously required importing `/esm/index.js` explicitly under webpack 4.
import { ImageResize } from 'tiptap-extension-resize-image';
import Link from '@tiptap/extension-link';
import Underline from '@tiptap/extension-underline';
import TextAlign from '@tiptap/extension-text-align';
import TextStyle from '@tiptap/extension-text-style';
import Color from '@tiptap/extension-color';
import FontFamily from '@tiptap/extension-font-family';
import Table from '@tiptap/extension-table';
import TableRow from '@tiptap/extension-table-row';
import TableCell from '@tiptap/extension-table-cell';
import TableHeader from '@tiptap/extension-table-header';

import { MergeTags } from './merge_tags';
import { buildMentions } from './mentions';
import { FontSize } from './font_size';
import { buildToolbar, insertImage } from './toolbar';
import { PageBreak } from './page_break';
import { buildBubbleMenus, buildBubbleHeader } from './bubble_menus';

// Uploads a single image file to the given endpoint and resolves to its servable URL.
// Used by editors that opt into upload (uploadUrl) so inline images are stored instead
// of embedded as base64 — keeping the submitted body small (avoids the nginx 413).
function uploadImageFile(uploadUrl, file) {
  const formData = new FormData();
  formData.append('image', file);
  const csrfToken = document.querySelector('meta[name="csrf-token"]');
  const headers = csrfToken ? { 'X-CSRF-Token': csrfToken.content } : {};

  return fetch(uploadUrl, { method: 'POST', headers, body: formData }).then((response) => {
    if (!response.ok) throw new Error(`Image upload failed: ${response.status}`);
    return response.json().then((data) => data.url);
  });
}

// ImageResize stores size and alignment in its own `containerstyle`/`wrapperstyle`
// attributes, which only its NodeView reads: outside the editor (WickedPdf, browser
// preview, e-mail) they are ignored and the image is printed at its intrinsic size.
// Mirroring them into a real `style` keeps the generated document faithful to the
// layout. The original attributes are kept, so parsing back into the editor — and
// the resize/align controls — behave exactly as before.
const PdfSafeImage = ImageResize.extend({
  renderHTML({ HTMLAttributes }) {
    const declarations = (HTMLAttributes.containerStyle || '')
      .split(';')
      .map((declaration) => declaration.trim())
      // `cursor` only hints the editor's resize handles; it means nothing once printed.
      .filter((declaration) => declaration && !/^cursor\s*:/i.test(declaration));

    if (!declarations.length) {
      return ['img', mergeAttributes(this.options.HTMLAttributes, HTMLAttributes)];
    }
    // The extension aligns through `margin: 0 auto`, which only takes effect on a
    // block box — inside the editor the flex wrapper provided it.
    if (!declarations.some((declaration) => /^display\s*:/i.test(declaration))) {
      declarations.push('display: block');
    }

    const style = `${declarations.join('; ')};`;
    return ['img', mergeAttributes(this.options.HTMLAttributes, HTMLAttributes, { style })];
  }
});

// Image files carried by a paste/drop event, if any.
function imageFilesFrom(dataTransfer) {
  if (!dataTransfer) return [];
  return Array.from(dataTransfer.files || []).filter((file) => file.type.startsWith('image/'));
}

// Single source of truth for spinning up a Tiptap editor over a <textarea>. Every
// contract/document/expression editor in the app goes through here so the extension
// set, the textarea syncing and the toolbar stay identical and DRY.
//
// The <textarea> remains the form's value holder: it's hidden and its value is
// rewritten on every change, so the server side (strong params, dictionary.translate,
// WickedPDF) is untouched regardless of which editor screen mounts it.
//
// options:
//   groups       → dictionary groups; enables the "{{" merge-tag autocomplete + button
//   mentionUrl   → endpoint returning [{ id, label }]; enables the "@" mention dropdown
//   onPreview    → optional callback wired to a toolbar preview button
//   extraButtons → [{ label, title, onClick, accent }] appended to the toolbar
//   variant      → 'full' (default) | 'minimal' (slim bar: merge tags + extras only)
//                  | 'bubble' (headless: no fixed toolbar; bubble menu on selection +
//                    floating menu on empty lines — for lightweight fields like ticket replies)
//   floatingMenu → bubble variant only; false disables the empty-line floating menu (default true)
//   stickyToolbar → pins the fixed toolbar to the top of the scrolling ancestor (drawers/modals)
//
// Returns the Editor instance, augmented with `host` (its container) so callers can
// tear it down or rebuild it (e.g. when the document's variable set changes).
export function mountTiptapEditor(textarea, options = {}) {
  const variant = options.variant || 'full';
  const host = document.createElement('div');
  host.className = variant === 'bubble' ? 'tr-editor tr-editor--bubble' : 'tr-editor';
  // Only the fixed toolbar can be pinned; the bubble variant has no bar to stick.
  if (options.stickyToolbar && variant !== 'bubble') host.classList.add('tr-editor--sticky-toolbar');
  textarea.style.display = 'none';
  textarea.parentNode.insertBefore(host, textarea.nextSibling);

  // When an upload endpoint is configured, pasted/dropped images are uploaded and
  // inserted as URLs instead of the default base64 embedding.
  const onUploadImage = options.uploadUrl ? (file) => uploadImageFile(options.uploadUrl, file) : null;

  // The bubble variant keeps a lean extension set (no font/size/color/table/align chrome,
  // since there is no toolbar exposing them) and mounts the bubble/floating menus instead.
  const bubbleMenus = variant === 'bubble'
    ? buildBubbleMenus({ host, onUploadImage, floatingMenu: options.floatingMenu !== false })
    : null;

  const extensions = variant === 'bubble'
    ? [
        StarterKit,
        Underline,
        Link.configure({ openOnClick: false }),
        PdfSafeImage.configure({ allowBase64: true }),
        // TextStyle + Color back the header's text-color control; FontSize backs the
        // increase/decrease font steppers (header + floating menu).
        TextStyle,
        Color,
        FontSize
      ]
    : [
        StarterKit,
        Underline,
        Link.configure({ openOnClick: false }),
        // PdfSafeImage (ImageResize) replaces the bare Image node: it keeps base64 data-URIs (allowBase64,
        // which the default Image strips on parse — the inline-image-loss bug) and adds the
        // drag-resize handles + a floating align (left/center/right) controller for free.
        PdfSafeImage.configure({ allowBase64: true }),
        TextStyle,
        Color,
        FontFamily,
        FontSize,
        TextAlign.configure({ types: ['heading', 'paragraph'] }),
        PageBreak,
        Table.configure({ resizable: true }),
        TableRow,
        TableHeader,
        TableCell
      ];
  if (options.groups && options.groups.length) {
    extensions.push(MergeTags.configure({ groups: options.groups }));
  }
  if (options.mentionUrl) {
    extensions.push(buildMentions(options.mentionUrl));
  }
  // BubbleMenu/FloatingMenu must be present at construction time.
  if (bubbleMenus) extensions.push(...bubbleMenus.extensions);

  const editorProps = onUploadImage ? { handlePaste: handleImageEvent, handleDrop: handleImageEvent } : {};
  // Bubble variant: Escape dismisses the open bubble/floating menu.
  if (bubbleMenus) editorProps.handleKeyDown = bubbleMenus.handleKeyDown;

  const editor = new Editor({
    element: host,
    extensions,
    editorProps,
    content: textarea.value || '',
    onUpdate: ({ editor: instance }) => {
      textarea.value = instance.getHTML();
    }
  });

  // Intercepts image files from paste/drop, uploads them, and prevents the default
  // base64 insertion. Returns false for non-image payloads so normal handling applies.
  function handleImageEvent(view, event) {
    const files = imageFilesFrom(event.clipboardData || event.dataTransfer);
    if (!files.length) return false;

    event.preventDefault();
    files.forEach((file) => insertImage(editor, file, onUploadImage));
    return true;
  }

  if (bubbleMenus) {
    // Bubble variant: wire the selection/empty-line menus, plus a slim fixed header
    // with the essential controls (color, bold, italic, link, image, horizontal rule).
    bubbleMenus.bindEditor(editor);
    const header = buildBubbleHeader(editor, { onUploadImage, floating: bubbleMenus.floating });
    host.insertBefore(header, host.firstChild);
  } else {
    const toolbar = buildToolbar(editor, {
      groups: options.groups || [],
      onPreview: options.onPreview || null,
      extraButtons: options.extraButtons || [],
      variant,
      onUploadImage
    });
    host.insertBefore(toolbar, host.firstChild);
  }

  editor.host = host;
  return editor;
}

// Tears down an editor and removes its container, restoring the original textarea
// (kept hidden) as the lone child so it can be remounted cleanly.
export function destroyTiptapEditor(editor) {
  if (!editor) return;
  const { host } = editor;
  editor.destroy();
  if (host && host.parentNode) host.parentNode.removeChild(host);
}
